/**
 * Recent Activity Feed
 * 
 * Light feed showing last 5 delivery events.
 * Simple, informational - not a full audit log.
 * 
 * PRINCIPLES:
 * - Show only client-relevant events
 * - Human-readable descriptions
 * - Clean timeline presentation
 */

import { 
  CheckCircle, 
  Gift, 
  RefreshCw,
  User,
  Clock
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";

export type ActivityType = 'lead_delivered' | 'credit_applied' | 'delivery_adjustment' | 'lead_returned';

export interface ActivityItem {
  id: string;
  type: ActivityType;
  title: string;
  description?: string;
  timestamp: Date;
}

interface RecentActivityFeedProps {
  activities: ActivityItem[];
  maxItems?: number;
}

const activityConfig: Record<ActivityType, {
  icon: typeof CheckCircle;
  iconClass: string;
  bgClass: string;
}> = {
  lead_delivered: {
    icon: User,
    iconClass: "text-green-600",
    bgClass: "bg-green-50",
  },
  credit_applied: {
    icon: Gift,
    iconClass: "text-accent",
    bgClass: "bg-accent/10",
  },
  delivery_adjustment: {
    icon: RefreshCw,
    iconClass: "text-blue-600",
    bgClass: "bg-blue-50",
  },
  lead_returned: {
    icon: RefreshCw,
    iconClass: "text-orange-600",
    bgClass: "bg-orange-50",
  },
};

export function RecentActivityFeed({ activities, maxItems = 5 }: RecentActivityFeedProps) {
  const displayActivities = activities.slice(0, maxItems);

  if (displayActivities.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="w-5 h-5 text-muted-foreground" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6 text-muted-foreground">
            <p className="text-sm">No recent activity</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Clock className="w-5 h-5 text-muted-foreground" />
          Recent Activity
        </CardTitle>
      </CardHeader>
      
      <CardContent>
        <div className="space-y-4">
          {displayActivities.map((activity, index) => {
            const config = activityConfig[activity.type];
            const Icon = config.icon;
            const isLast = index === displayActivities.length - 1;

            return (
              <div key={activity.id} className="flex gap-3">
                {/* Timeline connector */}
                <div className="flex flex-col items-center">
                  <div className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center",
                    config.bgClass
                  )}>
                    <Icon className={cn("w-4 h-4", config.iconClass)} />
                  </div>
                  {!isLast && (
                    <div className="w-px h-full bg-border flex-1 my-1" />
                  )}
                </div>
                
                {/* Content */}
                <div className="flex-1 pb-4">
                  <p className="font-medium text-foreground text-sm">{activity.title}</p>
                  {activity.description && (
                    <p className="text-sm text-muted-foreground">{activity.description}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatDistanceToNow(activity.timestamp, { addSuffix: true })}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
